'use client'
import { ChevronLeftIcon, ChevronRightIcon } from '@lastbrand/web-ui/icons'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
  type CarouselApi,
} from '@src/ds/blocks/ds-carousel'
import horizontalScrollCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/HorizontalScrollCacheStateManager'

import SectionRenderer from '../../SectionRenderer'
import { SectionProps } from '../../SectionRenderer.types'

import {
  carouselContentCss,
  carouselItemCss,
  carouselNextArrowCss,
  carouselPrevArrowCss,
  carouselRootCss,
  sectionWrapperClass,
} from './hero-carousel-ds-styles'

// DS variant of HeroCarousel: swaps the legacy swiper.js atom for the Embla-based
// `@lastbrand/web-ui/carousel`. Slide CONTENT is still rendered recursively through SectionRenderer,
// so each sub-section routes to its own DS/legacy branch and keeps firing its own impression/click
// telemetry via withCustomSectionWrapper — this section adds no telemetry of its own (parity: the
// legacy HeroCarousel passed no `tracker` to Swiper, so its arrows fired nothing).
//
// Tuned for parity with the legacy Swiper config (Swiper.tsx defaults, since HeroCarousel passed none):
// - free-scroll momentum (`dragFree`) + content-width slides (`carouselItemCss` / `.swiperSlide width:auto`)
// - arrows: DS chevron glyph, bottom-right, all viewports, shown when >1 slide, dimmed at boundaries
// - no dots (legacy `showDots` false); `align: 'start'`; `key={id}` remount (legacy `key={id}`)

// Arrow glyph size — legacy `icon 4rem ≈ 40px` (see hero-carousel-ds-styles.ts). Shared by both chevrons.
const ARROW_ICON_SIZE = '40px'

// Both buttons consume the Carousel context, so this must render inside <Carousel>.
function HeroCarouselArrows() {
  const { t } = useTranslation('common')
  return (
    <>
      <CarouselPreviousButton asChild css={carouselPrevArrowCss} aria-label={t('previous')}>
        <button type="button">
          <ChevronLeftIcon width={ARROW_ICON_SIZE} height={ARROW_ICON_SIZE} />
        </button>
      </CarouselPreviousButton>
      <CarouselNextButton asChild css={carouselNextArrowCss} aria-label={t('next')}>
        <button type="button">
          <ChevronRightIcon width={ARROW_ICON_SIZE} height={ARROW_ICON_SIZE} />
        </button>
      </CarouselNextButton>
    </>
  )
}

// @i18n-ignore — common.json key for the carousel landmark name
const HERO_CAROUSEL_ARIA_LABEL_KEY = 'hero_carousel_aria_label'
// @i18n-ignore — ARIA role keyword, not user-facing copy
const PRESENTATION_ROLE = 'presentation'

function HeroCarouselDS({ sectionConfig: { subSections, id, tracker, trackingMetaData, sectionIndex } }: SectionProps) {
  const { t } = useTranslation('common')
  const sectionRenderer = (idx: number) =>
    SectionRenderer({
      tracker: tracker && `${tracker}__heroCarousel`,
      sectionIndex: idx,
    })

  // Legacy hid the whole arrow set when slides <= 1 (`hideSlideArrows`).
  const slideCount = subSections?.length ?? 0
  const showArrows = slideCount > 1

  // Restore the active slide on back-navigation — parity with the legacy Swiper's
  // horizontalScrollCacheStateManager. Embla is index/snap-based, so (unlike the legacy translate-offset
  // cache) we persist the nearest settled snap index and jump to it on init. Same cache key shape as legacy.
  const slideIndexCacheKey = `${trackingMetaData?.context?.id ?? id}_activeIndex`
  const [emblaApi, setEmblaApi] = useState<CarouselApi>()

  useEffect(() => {
    if (!emblaApi) return
    const cachedIndex = horizontalScrollCacheStateManager.get<number>(slideIndexCacheKey)
    if (cachedIndex && cachedIndex > 0) emblaApi.scrollTo(cachedIndex, true) // jump, no animation

    const onSelect = () => {
      horizontalScrollCacheStateManager.set(slideIndexCacheKey, emblaApi.selectedScrollSnap())
    }
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, slideIndexCacheKey])

  // Single-slide wrappers are not interactive carousels — drop the region landmark so multiple
  // heroes on one page don't fail landmark-unique. Multi-slide carousels get a unique aria-label
  // from the 1-based page section index (not the Contentful entry id hash).
  const carouselOrdinal = (sectionIndex ?? 0) + 1
  const carouselA11yProps =
    slideCount > 1
      ? { 'aria-label': `${t(HERO_CAROUSEL_ARIA_LABEL_KEY)} ${carouselOrdinal}` }
      : { role: PRESENTATION_ROLE, 'aria-roledescription': undefined }

  return (
    <Carousel
      key={id}
      css={carouselRootCss}
      opts={{ dragFree: true, align: 'start' }}
      setEmblaApi={setEmblaApi}
      {...carouselA11yProps}
    >
      <CarouselContent css={carouselContentCss}>
        {subSections?.map((subSection, idx) => (
          <CarouselItem key={subSection.id} css={carouselItemCss}>
            <div className={sectionWrapperClass} data-dd-action-name="hero slide">
              {sectionRenderer(idx)({
                ...subSection,
                trackingMetaData: {
                  ...trackingMetaData,
                  context: {
                    ...trackingMetaData?.context,
                    horizontalPosition: idx + 1,
                  },
                },
              })}
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      {showArrows && <HeroCarouselArrows />}
    </Carousel>
  )
}

export default HeroCarouselDS
